import React, { useState } from 'react';
import { Globe, X, Check } from 'lucide-react';
import { useApp } from '../context/AppContext';

const languages = [
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  { code: 'sv', name: 'Svenska', flag: '🇸🇪' },
];

const FloatingLanguageSelector = () => {
  const { language, setLanguage } = useApp();
  const [isOpen, setIsOpen] = useState(false);

  const currentLanguage = languages.find(l => l.code === language) || languages[0];

  const handleLanguageSelect = (langCode) => {
    setLanguage(langCode);
    setIsOpen(false);
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 left-6 z-50 flex items-center gap-2 px-4 py-3 rounded-full 
                   bg-gradient-to-r from-[#1C1917] to-[#2C1B18] 
                   border border-[#D4AF37]/30 hover:border-[#D4AF37] 
                   shadow-lg shadow-black/50 hover:shadow-[#D4AF37]/20
                   transition-all duration-300 group"
        data-testid="floating-language-btn"
      >
        <Globe 
          size={20} 
          className="text-[#D4AF37] group-hover:rotate-12 transition-transform duration-300" 
        />
        <span className="text-white text-sm font-medium">{currentLanguage.flag}</span>
        <span className="text-[#A8A29E] text-sm hidden sm:inline">{currentLanguage.name}</span>
      </button>

      {/* Language Selector Modal */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4"
          onClick={() => setIsOpen(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          
          {/* Modal Content */}
          <div 
            className="relative w-full max-w-sm bg-gradient-to-b from-[#1C1917] to-[#0D0D0D] 
                       rounded-2xl border border-[#D4AF37]/20 shadow-2xl shadow-black/50
                       overflow-hidden animate-in slide-in-from-bottom duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-[#2C1B18]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                  <Globe size={20} className="text-[#D4AF37]" />
                </div>
                <div>
                  <h3 className="text-white font-medium">Kies je taal</h3>
                  <p className="text-[#57534E] text-xs">Select your language</p>
                </div>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-white/5 rounded-lg text-[#A8A29E] hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Language Grid */}
            <div className="p-4 grid grid-cols-3 gap-2 max-h-80 overflow-y-auto">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageSelect(lang.code)}
                  className={`flex flex-col items-center gap-2 p-3 rounded-xl transition-all duration-200
                    ${language === lang.code 
                      ? 'bg-[#D4AF37]/20 border border-[#D4AF37] ring-2 ring-[#D4AF37]/30' 
                      : 'bg-[#2C1B18]/50 border border-transparent hover:border-[#D4AF37]/30 hover:bg-[#2C1B18]'
                    }`}
                  data-testid={`lang-option-${lang.code}`}
                >
                  <span className="text-2xl">{lang.flag}</span>
                  <span className={`text-xs font-medium truncate w-full text-center
                    ${language === lang.code ? 'text-[#D4AF37]' : 'text-[#A8A29E]'}`}>
                    {lang.name}
                  </span>
                  {language === lang.code && (
                    <Check size={14} className="text-[#D4AF37] absolute top-1 right-1" />
                  )}
                </button>
              ))}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-[#2C1B18] bg-[#0D0D0D]/50">
              <p className="text-[#57534E] text-xs text-center">
                🌍 9 talen beschikbaar
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingLanguageSelector;
